import React from "react";
import { graphql } from "gatsby";
import tw, { css } from "twin.macro";
import { GatsbyImage, getImage } from "gatsby-plugin-image";
import { DotMatrix, BlogCardBlock, CTA, SEO } from "../components";

const proseStyles = css`
  & * {
    ${tw`prose! prose-lg!`}
  }
  & p,
  & p > span,
  & blockquote,
  & blockquote > span {
    ${tw`text-gray-500!`}
  }
  & h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    ${tw`text-gray-800!`}
  }
`;

const BlogpostTemplate = ({ data: { strapiArticle } }) => {
  const {
    content,
    author: { picture, name: authorName },
    category: { name: category },
    description,
    image,
    publishedAt,
    title,
    slug,
  } = strapiArticle;
  return (
    <>
      <SEO
        title={title}
        description={description}
        previewImage={{ src: image.localFile.publicURL }}
      />
      <main>
        <section tw="relative py-16 bg-white overflow-hidden">
          <DotMatrix side="right" positioning={tw`top-12`} />
          <DotMatrix side="left" positioning={tw`top-1/2`} />
          <div tw="relative mx-auto max-w-prose px-4 sm:px-6 lg:px-8">
            <div tw="text-lg max-w-prose mx-auto">
              <span tw="block font-semibold text-base text-dsrTeal-600 text-center tracking-wide uppercase">
                {`Blog / ${category}`}
              </span>
              <h1 tw="mt-2 block text-5xl text-center font-extrabold tracking-tight text-gray-900 sm:text-5xl">
                {title}
              </h1>
              {!!description && (
                <p tw="mt-8 text-xl text-gray-500 leading-8">{description}</p>
              )}
            </div>
            <div tw="mt-20 mx-auto text-gray-500">
              <figure tw="rounded-lg">
                <GatsbyImage
                  image={getImage(image.localFile)}
                  alt={
                    image.alternativeText ||
                    image.caption ||
                    `${title} hero image`
                  }
                  tw="w-full rounded-lg"
                  imgStyle={{
                    borderRadius: "0.5rem",
                    marginTop: 0,
                    marginBottom: 0,
                  }}
                />
                {!!image.caption && (
                  <figcaption tw="text-center">{image.caption}</figcaption>
                )}
              </figure>
              <div tw="prose prose-lg max-w-prose mt-20 mx-auto">
                <div
                  className="strapi-html-content"
                  css={[proseStyles]}
                  dangerouslySetInnerHTML={{ __html: content }}
                />
              </div>
            </div>
          </div>
        </section>
        <BlogCardBlock slug={slug} />
        <CTA />
      </main>
    </>
  );
};

export default BlogpostTemplate;

export const query = graphql`
  query blogpostQuery($id: String!) {
    strapiArticle(id: { eq: $id }) {
      content
      author {
        picture {
          id
          localFile {
            childImageSharp {
              gatsbyImageData(width: 50)
            }
          }
        }
        name
      }
      category {
        name
      }
      description
      image {
        id
        caption
        alternativeText
        localFile {
          childImageSharp {
            gatsbyImageData(width: 500)
          }
          publicURL
        }
      }
      publishedAt
      title
      slug
    }
  }
`;
